import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { X, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { api, type RegistryItem } from '../../services/api.js';
import { useToast } from '../shared/Toast.js';
import { lockScroll, unlockScroll } from '../../utils/scrollLock.js';

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

export function CoinRegistryModal({ onClose, onSaved }: Props) {
  const [items, setItems] = useState<RegistryItem[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, visible: 0 });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const { show: toast } = useToast();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Load stats on mount
  useEffect(() => {
    api.getRegistryStats().then(setStats).catch(() => {});
  }, []);

  // Search with debounce
  const doSearch = useCallback((q: string, p: number) => {
    setLoading(true);
    api.searchRegistry(q, p).then((res) => {
      setItems(res.items);
      setPages(res.pages);
      setTotal(res.total);
      setPage(res.page);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  // Initial load
  useEffect(() => {
    doSearch('', 1);
  }, [doSearch]);

  // Lock the #app scroller while the modal is open
  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSearchInput = (q: string) => {
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(q, 1);
    }, 300);
  };

  const handleToggle = async (coin: RegistryItem) => {
    const newVisible = !coin.visible;
    setToggling(coin.id);

    // Optimistic update
    setItems((prev) => prev.map((c) => c.id === coin.id ? { ...c, visible: newVisible } : c));
    setStats((s) => ({
      ...s,
      visible: s.visible + (newVisible ? 1 : -1),
    }));

    try {
      await api.toggleCoin(coin.id, newVisible);
      setDirty(true);
    } catch {
      // Revert
      setItems((prev) => prev.map((c) => c.id === coin.id ? { ...c, visible: !newVisible } : c));
      setStats((s) => ({
        ...s,
        visible: s.visible + (newVisible ? -1 : 1),
      }));
      toast('Ошибка переключения', 'error');
    } finally {
      setToggling(null);
    }
  };

  const handleClose = () => {
    if (dirty) {
      onSaved();
    } else {
      onClose();
    }
  };

  return (
    <>
      <div class="modal-backdrop" onClick={handleClose} />
      <div class="modal" role="dialog" aria-modal="true">
        <div class="modal-header">
          <span class="modal-title">Видимость монет</span>
          <button class="btn-icon" onClick={handleClose} style={{ width: '28px', height: '28px' }}>
            <X size={14} />
          </button>
        </div>

        <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--bd-dim)' }}>
          <div style={{ position: 'relative' }}>
            <span
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-muted)', display: 'flex' }}
            ><MagnifyingGlass size={14} /></span>
            <input
              type="search"
              class="setting-input"
              style={{ width: '100%', paddingLeft: '32px', textAlign: 'left' }}
              placeholder={`Поиск по ${stats.total} монетам...`}
              value={search}
              onInput={(e) => handleSearchInput((e.target as HTMLInputElement).value)}
              autoFocus
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-2)' }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-secondary)' }}>
              <strong style={{ color: 'var(--lime)' }}>{stats.visible}</strong> из {stats.total} видимых
              {search && ` (найдено: ${total})`}
            </span>
          </div>
        </div>

        <div class="modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--tx-muted)' }}>Загрузка...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--tx-muted)' }}>Ничего не найдено</div>
          ) : (
            items.map((coin) => (
              <div
                key={coin.id}
                class="registry-row"
                style={{ opacity: toggling === coin.id ? 0.5 : 1 }}
              >
                <span class="registry-symbol">
                  {coin.symbol.toUpperCase()}
                </span>
                <span class="registry-name">
                  {coin.name}
                </span>
                <label class="toggle" style={{ flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={coin.visible}
                    onChange={() => handleToggle(coin)}
                    disabled={toggling !== null}
                  />
                  <span class="toggle-track" />
                  <span class="toggle-thumb" />
                </label>
              </div>
            ))
          )}
        </div>

        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2)', borderTop: '1px solid var(--bd-dim)' }}>
            <button class="btn btn-secondary" disabled={page <= 1} onClick={() => doSearch(search, 1)} style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }}>«</button>
            <button class="btn btn-secondary" disabled={page <= 1} onClick={() => doSearch(search, page - 1)} style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }}>Назад</button>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--tx-secondary)' }}>
              {page} / {pages}
            </span>
            <button class="btn btn-secondary" disabled={page >= pages} onClick={() => doSearch(search, page + 1)} style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }}>Вперёд</button>
            <button class="btn btn-secondary" disabled={page >= pages} onClick={() => doSearch(search, pages)} style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }}>»</button>
          </div>
        )}

        <div class="modal-footer">
          <button class="btn btn-primary" onClick={handleClose}>
            <Check size={14} />
            <span>Готово</span>
          </button>
        </div>
      </div>
    </>
  );
}
